import React, { useState } from "react";
import Table from "react-bootstrap/Table";
import { styles } from "../../styles/styles.js";
import * as motion from "motion/react-client";
import ReactMarkdown from "react-markdown";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ReferenceLine,
  Tooltip,
  Legend,
} from "recharts";
import { EM_DASH, isNum, fmt, diff } from "../../utils/format.js";

// Raw "PF - PA" pair. Either half unknown makes the pair unknown: "108 - null"
// is noise, and a substituted 0 is a shutout that never happened.
const pair = (pointsFor, pointsAgainst) =>
  isNum(pointsFor) && isNum(pointsAgainst) ? `${pointsFor} - ${pointsAgainst}` : EM_DASH;

const fontFamily =
  "source-code-pro, Menlo, Monaco, Consolas, 'Courier New', monospace";

const tickStyle = {
  fill: "white",
  fontSize: "clamp(10px, calc(7px + (4) * ((100vw - 300px) / (1200 - 320))), 18px)",
  fontFamily,
};

export const Chart = ({ fixture, mode = 'standard' }) => {
  const chartData = [];
  let minY = 0;
  let maxY = 0;

  // A game only enters the domain once we know its differential. Recharts draws
  // a null as a gap in the line; a 0 would draw a real point at the origin, so a
  // team with no history would look like it drew every game.
  const fold = (value) => {
    if (!isNum(value)) return;
    minY = Math.min(minY, value);
    maxY = Math.max(maxY, value);
  };

  for (let i = 10; i >= 1; i--) {
    if (mode === 'h2h') {
      const winnerPoints = fixture.head_to_head?.[`h2h_${i}_winner_points`];
      const loserPoints = fixture.head_to_head?.[`h2h_${i}_loser_points`];
      const winner = fixture.head_to_head?.[`h2h_${i}_winner`];
      // Without a winner the sign of the differential is unknowable, so the
      // point is unknown rather than silently charged to the away side.
      const h2hDiff = winner == null
        ? null
        : winner === fixture.home_team ? diff(winnerPoints, loserPoints) : diff(loserPoints, winnerPoints);
      chartData.push({ game: 11 - i, h2h: h2hDiff });
      fold(h2hDiff);
    } else {
      const homeWinnerPoints = fixture.recent?.[`home_${i}_winner_points`];
      const homeLoserPoints = fixture.recent?.[`home_${i}_loser_points`];
      const homeWinner = fixture.recent?.[`home_${i}_winner`];
      const homeDiff = homeWinner == null
        ? null
        : homeWinner === fixture.home_team ? diff(homeWinnerPoints, homeLoserPoints) : diff(homeLoserPoints, homeWinnerPoints);

      const awayWinnerPoints = fixture.recent?.[`away_${i}_winner_points`];
      const awayLoserPoints = fixture.recent?.[`away_${i}_loser_points`];
      const awayWinner = fixture.recent?.[`away_${i}_winner`];
      const awayDiff = awayWinner == null
        ? null
        : awayWinner === fixture.away_team ? diff(awayWinnerPoints, awayLoserPoints) : diff(awayLoserPoints, awayWinnerPoints);

      chartData.push({ game: 11 - i, home: homeDiff, away: awayDiff });
      fold(homeDiff);
      fold(awayDiff);
    }
  }

  // Both bounds are seeded at 0 and only ever folded with known values, so an
  // all-unknown chart still yields a finite [-5, 5] axis instead of the
  // Infinity/-Infinity an empty spread would produce.
  minY = Math.floor(minY - 5);
  maxY = Math.ceil(maxY + 5);

  const ticks = Array.from({ length: 12 }, (_, i) => i + 1);

  return (
    <ResponsiveContainer width="100%" height={styles.height || 300}>
      <LineChart data={chartData}>
        <CartesianGrid
          stroke="rgba(255, 255, 255, 0.2)"
          horizontal={true}
          vertical={false}
        />
        <XAxis
          dataKey="game"
          type="number"
          domain={[1, 12]}
          ticks={ticks}
          label={{ value: "Games", position: "insideBottom", offset: -5, style: tickStyle }}
          tick={{ style: tickStyle }}
          stroke="white"
        />
        <YAxis
          domain={[minY, maxY]}
          label={{ value: "Point Differential", angle: -90, position: "insideLeft", offset: 10, style: tickStyle }}
          tick={{ style: tickStyle }}
          stroke="white"
        />
        <ReferenceLine y={0} stroke="white" strokeDasharray="3 3" />
        <Tooltip
          contentStyle={{ backgroundColor: "#1a1a1a", border: "1px solid #333", fontFamily }}
          labelStyle={{ color: "white" }}
          itemStyle={{ fontFamily }}
        />
        <Legend wrapperStyle={{ ...tickStyle, paddingTop: "10px" }} />
        {mode === 'h2h' ? (
          <Line
            type="linear"
            dataKey="h2h"
            name="H2H Point Differential"
            stroke="white"
            dot={false}
            strokeWidth={2}
          />
        ) : (
          <>
            <Line
              type="linear"
              dataKey="home"
              name={fixture.home_team}
              stroke="#20B2AA"
              dot={false}
              strokeWidth={2}
            />
            <Line
              type="linear"
              dataKey="away"
              name={fixture.away_team}
              stroke="red"
              dot={false}
              strokeWidth={2}
              strokeDasharray="5 5"
            />
          </>
        )}
      </LineChart>
    </ResponsiveContainer>
  );
};


const PointDiff = ({ fixture }) => {
  const [showPointDifference, setShowPointDifference] = useState(true);

  const togglePointDifference = () => {
    setShowPointDifference(!showPointDifference);
  };

  const AnimatedCell = ({ children }) => (
    <motion.div
      key={showPointDifference}
      initial={{ opacity: 0, x: -100 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{
        duration: 0.5,
        scale: { type: "spring", damping: 10, stiffness: 100, bounce: 0.5 },
      }}>
      {children}
    </motion.div>
  );

  if (!fixture.recent) return null;

  return (
    <>
      <Table bordered className="main">
        <tbody>
          <tr style={styles.Cells}>
            <td className="Cells" style={styles.column_1} onClick={togglePointDifference}>
              Point Diff
            </td>
            <td className="Cells" style={styles.Cells}></td>
            <td className="Cells" style={styles.Cells}></td>
          </tr>
          <tr>
            <td className="Cells" style={styles.column_1} onClick={togglePointDifference}></td>

            <td className="Cells" style={{ ...styles.column_1, color: '#40E0D0' }}>
              {fixture.home_team}
            </td>
            <td className="Cells" style={{ ...styles.column_1, color: 'red' }}>
              {fixture.away_team}
            </td>
          </tr>
          <tr style={styles.Cells}>
            <td className="Cells" style={styles.column_1} onClick={togglePointDifference}>
              Last 3
            </td>
            <td className="Cells" style={styles.Cells}>
              <AnimatedCell>
                {showPointDifference
                  ? fmt(diff(fixture.recent.home_PF_3, fixture.recent.home_PA_3), 1, { signed: true })
                  : pair(fixture.recent.home_PF_3, fixture.recent.home_PA_3)}
              </AnimatedCell>
            </td>
            <td className="Cells" style={styles.Cells}>
              <AnimatedCell>
                {showPointDifference
                  ? fmt(diff(fixture.recent.away_PF_3, fixture.recent.away_PA_3), 1, { signed: true })
                  : pair(fixture.recent.away_PF_3, fixture.recent.away_PA_3)}
              </AnimatedCell>
            </td>
          </tr>
          <tr style={styles.Cells}>
            <td className="Cells" style={styles.column_1} onClick={togglePointDifference}>
              Last 5
            </td>
            <td className="Cells" style={styles.Cells}>
              <AnimatedCell>
                {showPointDifference
                  ? fmt(diff(fixture.recent.home_PF_5, fixture.recent.home_PA_5), 1, { signed: true })
                  : pair(fixture.recent.home_PF_5, fixture.recent.home_PA_5)}
              </AnimatedCell>
            </td>
            <td className="Cells" style={styles.Cells}>
              <AnimatedCell>
                {showPointDifference
                  ? fmt(diff(fixture.recent.away_PF_5, fixture.recent.away_PA_5), 1, { signed: true })
                  : pair(fixture.recent.away_PF_5, fixture.recent.away_PA_5)}
              </AnimatedCell>
            </td>
          </tr>
          <tr style={styles.Cells}>
            <td className="Cells" style={styles.column_1} onClick={togglePointDifference}>
              Last 10
            </td>
            <td className="Cells" style={styles.Cells}>
              <AnimatedCell>
                {showPointDifference
                  ? fmt(diff(fixture.recent.home_PF_10, fixture.recent.home_PA_10), 1, { signed: true })
                  : pair(fixture.recent.home_PF_10, fixture.recent.home_PA_10)}
              </AnimatedCell>
            </td>
            <td className="Cells" style={styles.Cells}>
              <AnimatedCell>
                {showPointDifference
                  ? fmt(diff(fixture.recent.away_PF_10, fixture.recent.away_PA_10), 1, { signed: true })
                  : pair(fixture.recent.away_PF_10, fixture.recent.away_PA_10)}
              </AnimatedCell>
            </td>
          </tr>
          <tr style={styles.Cells}>
            <td className="Cells" style={styles.column_1} onClick={togglePointDifference}>
              Season
            </td>
            <td className="Cells" style={styles.Cells}>
              <AnimatedCell>
                {showPointDifference
                  ? fmt(diff(fixture.stats?.home_PF, fixture.stats?.home_PA), 1, { signed: true })
                  : pair(fixture.stats?.home_PF, fixture.stats?.home_PA)}
              </AnimatedCell>
            </td>
            <td className="Cells" style={styles.Cells}>
              <AnimatedCell>
                {showPointDifference
                  ? fmt(diff(fixture.stats?.away_PF, fixture.stats?.away_PA), 1, { signed: true })
                  : pair(fixture.stats?.away_PF, fixture.stats?.away_PA)}
              </AnimatedCell>
            </td>
          </tr>
          {shouldShowLastSeason(fixture) && (
            <tr style={styles.Cells}>
              <td className="Cells" style={styles.column_1} onClick={togglePointDifference}>
                Last Season
              </td>
              <td className="Cells" style={styles.Cells}>
                <AnimatedCell>
                  {showPointDifference
                    ? fmt(diff(fixture.stats?.home_PF_last, fixture.stats?.home_PA_last), 1, { signed: true })
                    : pair(fixture.stats?.home_PF_last, fixture.stats?.home_PA_last)}
                </AnimatedCell>
              </td>
              <td className="Cells" style={styles.Cells}>
                <AnimatedCell>
                  {showPointDifference
                    ? fmt(diff(fixture.stats?.away_PF_last, fixture.stats?.away_PA_last), 1, { signed: true })
                    : pair(fixture.stats?.away_PF_last, fixture.stats?.away_PA_last)}
                </AnimatedCell>
              </td>
            </tr>
          )}
        </tbody>
      </Table>
      <Chart fixture={fixture} />

      <div style={{ height: "10vh" }}></div>

      <div style={{ marginTop: "20px" }}>
        {fixture.box_score && (
          <ul
            style={{
              listStyleType: "none",
              padding: 0,
              margin: 0,
              fontSize: "clamp(10px, calc(7px + (4) * ((100vw - 300px) / (1200 - 320))), 18px)",
            }}>
            <ReactMarkdown>{fixture.box_score["pointdiff_stats"]}</ReactMarkdown>
          </ul>
        )}
      </div>
    </>
  );
};

function shouldShowLastSeason(fixture) {
  // Unknown season progress is not early-season. Showing a "Last Season" row we
  // cannot justify is worse than omitting it, and the old unguarded deref threw
  // on every fixture without ratings — this file had no league short-circuit to
  // hide behind.
  if (fixture.ratings?.progress == null) {
    return false;
  }
  if (fixture.league === "NFL") {
    return fixture.ratings.progress < 0.2;
  }
  return fixture.ratings.progress < 0.15;
}

export default PointDiff;
